import { Icon } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import React, { Dispatch, SetStateAction, useCallback, useContext, useMemo } from 'react';
import { RCSubMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBIconV2Name } from '@shared/components/image';
import { MBTextOverlineBold } from '@shared/components/typography';
import {
  GenericComponent,
  RelatedCardComponent,
  RelatedCardLookupComponent,
} from '@shared/scenes/workflows/config';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import {
  ListItem,
  SelectableItemList,
} from '@shared/scenes/workflows/edit/components/optionsMenu/referencedInOptions/SelectableItemList';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import {
  isAddressComponent,
  isDateComponent,
  isEmailComponent,
  isNumberComponent,
  isRelatedCardComponent,
  isRelatedCardLookupComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { NestedOptionsRow } from '../NestedOptionsRow';
import { MBWorkflowDefaultComponentOptions } from '../defaultOptions';

interface Props {
  component: RelatedCardLookupComponent;
  workflowTemplateId: string;
}

export function isComponentSupportedForLookup(component: GenericComponent) {
  return (
    isTextComponent(component) ||
    isNumberComponent(component) ||
    isDateComponent(component) ||
    isAddressComponent(component) ||
    isEmailComponent(component) ||
    isTagComponent(component)
  );
}

export function RelatedCardLookupOptions({ component, workflowTemplateId }: Props) {
  const extraSectionsGenerator = useOptions(workflowTemplateId, component);

  return (
    <MBWorkflowDefaultComponentOptions
      component={component}
      extraSections={extraSectionsGenerator}
    />
  );
}

function useOptions(workflowTemplateId: string, component: RelatedCardLookupComponent) {
  const dispatch = useWorkflowDispatch();
  const currentWorkflowTemplate = useWorkflowTemplate(workflowTemplateId);
  const relatedWorkflowTemplate = useWorkflowTemplate(component.lookupWorkflowTemplateId);
  const colors = useContext(ColorThemeContext);

  const workflowComponents = useMemo(() => {
    return currentWorkflowTemplate.workflowTemplate?.components ?? [];
  }, [currentWorkflowTemplate.workflowTemplate?.components]);

  const relatedWorkflowComponents = useMemo(() => {
    return relatedWorkflowTemplate?.workflowTemplate?.components ?? [];
  }, [relatedWorkflowTemplate?.workflowTemplate?.components]);

  const relatedCardComponents = useMemo(
    () => workflowComponents.filter(isRelatedCardComponent),
    [workflowComponents],
  );

  const availableLookupComponents = useMemo(
    () => relatedWorkflowComponents?.filter(isComponentSupportedForLookup),
    [relatedWorkflowComponents],
  );
  const isLookupEnabled = !isEmpty(availableLookupComponents);

  const savedComponent = workflowComponents.find(value => {
    return value.id === component.id && isRelatedCardLookupComponent(value);
  });
  const shouldAllowSelection = isNil(savedComponent);

  const selectedComponent = relatedCardComponents.find(c => {
    return c.id === component.relatedCardComponentId;
  });
  const selectedLookupComponent = availableLookupComponents.find(c => {
    return c.id === component.lookupComponentId;
  });

  const onRelatedCardComponentSelected = useCallback(
    (chosenComponentId: string, setOpenKeys: Dispatch<SetStateAction<string[]>>) => {
      const chosenComponent = relatedCardComponents.find(c => c.id === chosenComponentId);
      if (!chosenComponent) {
        return;
      }
      setOpenKeys(prev => [
        ...prev.filter(k => !['relatedCardRow', 'relatedCardLookup'].includes(k)),
        'relatedCardLookup',
      ]);
      const alreadySelected = component.relatedCardComponentId === chosenComponent.id;
      const chosenWorkflowTemplateId = chosenComponent.workflowTemplateId ?? '';
      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          relatedCardComponentId: !alreadySelected ? chosenComponent.id : '',
          lookupWorkflowTemplateId: !alreadySelected ? chosenWorkflowTemplateId : '',
        },
      });
    },
    [dispatch, component, relatedCardComponents],
  );

  const onLookupComponentSelected = useCallback(
    (chosenComponentId: string, setOpenKeys: Dispatch<SetStateAction<string[]>>) => {
      setOpenKeys(prev => [...prev.filter(k => k !== 'relatedCardLookup')]);
      const chosenComponent = availableLookupComponents.find(c => c.id === chosenComponentId);
      if (!chosenComponent || !selectedComponent) {
        return;
      }
      const alreadySelected = component.lookupComponentId === chosenComponent.id;
      const label = !alreadySelected
        ? `${chosenComponent.label} (from ${selectedComponent.label})`
        : i18n('workflowTemplate.component.type.label.relatedCardLookup');

      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          label,
          placeholder: label,
          icon: !alreadySelected ? chosenComponent.icon : 'search-look-up',
          lookupComponentId: !alreadySelected ? chosenComponent.id : '',
        },
      });
    },
    [dispatch, component, availableLookupComponents, selectedComponent],
  );

  const linkableComponentsListItems = useMemo(
    () => availableLookupComponents.map(toListItem),
    [availableLookupComponents],
  );

  const availableRelatedCardsListItems = useMemo(
    () => relatedCardComponents.map(toListItem),
    [relatedCardComponents],
  );

  return function Options({ setOpenKeys }) {
    return (
      <>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('builder.relatedCardLookup.selectedRelatedCardOptions')}
          </MBTextOverlineBold>
        </View>

        {shouldAllowSelection && (
          <RCSubMenu
            key="relatedCardRow"
            style={{ zIndex: 999 }}
            popupClassName="test-popup"
            title={
              <NestedOptionsRow
                name={
                  selectedComponent?.label ||
                  i18n('builder.relatedCardLookup.selectedRelatedCardOptionsRow')
                }
                defaultIcon="duplicate-squares"
                selectedIcon="duplicate-squares"
              />
            }
          >
            <View paddingVertical={1}>
              <SelectableItemList
                items={availableRelatedCardsListItems}
                onSelectItem={componentId => {
                  onRelatedCardComponentSelected(componentId, setOpenKeys);
                }}
                enabled={true}
                selectedItem={selectedComponent?.id}
              />
            </View>
          </RCSubMenu>
        )}
        {!shouldAllowSelection && (
          <NestedOptionsRow
            name={selectedComponent?.label ?? ''}
            defaultIcon="duplicate-squares"
            selectedIcon="duplicate-squares"
            enabled={false}
          />
        )}
        {isLookupEnabled && (
          <>
            {shouldAllowSelection && (
              <>
                <View paddingHorizontal={3} paddingVertical={1}>
                  <MBTextOverlineBold color={colors.foregroundInactive}>
                    {i18n('builder.relatedCardLookup.selectedRelatedCardLookupOptions')}
                  </MBTextOverlineBold>
                </View>
                <RCSubMenu
                  key="relatedCardLookup"
                  style={{ zIndex: 999 }}
                  forceSubMenuRender
                  title={
                    <NestedOptionsRow
                      name={
                        selectedLookupComponent?.label ||
                        i18n('builder.relatedCardOptions.singleFieldLookup')
                      }
                      defaultIcon={(selectedLookupComponent?.icon || 'search-look-up') as Icon}
                      selectedIcon={(selectedLookupComponent?.icon || 'search-look-up') as Icon}
                    />
                  }
                >
                  <View paddingVertical={1}>
                    <SelectableItemList
                      items={linkableComponentsListItems}
                      onSelectItem={componentId => {
                        onLookupComponentSelected(componentId, setOpenKeys);
                      }}
                      enabled={true}
                      selectedItem={selectedLookupComponent?.id}
                    />
                  </View>
                </RCSubMenu>
              </>
            )}
            {!shouldAllowSelection && (
              <NestedOptionsRow
                name={selectedLookupComponent?.label ?? ''}
                defaultIcon={(selectedLookupComponent?.icon || 'search-look-up') as Icon}
                selectedIcon={(selectedLookupComponent?.icon || 'search-look-up') as Icon}
                enabled={false}
              />
            )}
          </>
        )}
      </>
    );
  };
}

function toListItem(
  component:
    | RelatedCardComponent
    | RelatedCardLookupComponent
    | MBAPI.WorkflowTemplate_components_AddressComponent,
): ListItem {
  return { id: component.id, label: component.label ?? '', icon: component.icon as MBIconV2Name };
}
